/**
 * "Task-Template verwalten" - die Flaeche hinter dem gleichnamigen Eintrag des Marken-Menues.
 *
 * Herkunft: `openTaskTemplateManager` (index.html:5510-5524), `renderTaskTemplateManager` (:5599),
 * `renderResponsibilityModules` (:5556), `addTaskTemplateRow`/`removeTaskTemplateRow` (:5620/:5627),
 * `saveTaskTemplateManager` (:5633-5687) samt Huelle (:3859-3885).
 *
 * ============================== WARUM ES DIESE DATEI ERST JETZT GIBT ==============================
 *
 * Der Knopf stand seit Task 10 im Menue, OHNE `onClick` - ausdruecklich als "BENANNTE, keine stille
 * Luecke" gefuehrt (`App/MarkenMenue.tsx`-Kopfkommentar; `test/reference/bundle.test.ts`: "die EINE
 * verbleibende benannte Luecke des Marken-Menues ... ein eigener, hier nicht beauftragter Task").
 * Genau dieser Task ist am 2026-08-12 beauftragt worden: der Auftraggeber hat gemeldet, dass der
 * Knopf nichts tut. Damit ist die Luecke geschlossen und der Eintrag aus beiden Kommentaren
 * gestrichen.
 *
 * ============================== WAS DIESE FLAECHE KANN ==============================
 *
 * 1. ZUSTAENDIGKEIT je Modul setzen (Hauptverantwortung, Vertretung). Das ist der Teil mit der
 *    SOFORT messbaren Wirkung: `PATCH /api/responsibility-modules/:moduleId` uebertraegt beide
 *    Rollen in DERSELBEN Transaktion auf alle OFFENEN Tasks des Moduls
 *    (`backend/src/domaene/zustaendigkeit.ts#uebertrageVerantwortung`) und meldet als
 *    `uebertrageneTasks` zurueck, wie viele es waren - diese Zahl steht in der Erfolgsmeldung.
 *    Erledigte Tasks bleiben als Historie unberuehrt (Spec Zeile 689f.), frei angelegte Tasks ohne
 *    Modulbezug ebenfalls.
 * 2. Die STANDARDAUFGABEN der Vorlage pflegen (Phase, Name, Beschreibung, Modul).
 *
 * ============================== WAS DIESE FLAECHE NICHT KANN, UND WARUM ==============================
 *
 * ZWEI benannte Luecken, keine stillen - Luecke (a) sichtbar gemacht durch ein gesperrtes
 * Bedienelement mit `title`, nach dem Muster "Kurssicherung ohne `manage_fx`" (Auftraggeber-
 * Entscheidung 2026-08-11); Luecke (b) ist rein textlich (kein Bedienelement dafuer, siehe dort):
 *
 *  a) MODULE ANLEGEN, UMBENENNEN, LOESCHEN. Der Bestand konnte das (index.html:5578-5597); dieses
 *     Backend nicht. Unter `/api/responsibility-modules` gibt es GENAU ZWEI Endpunkte - GET und
 *     PATCH -, und `name` steht in keiner Schreiballowlist (`endpunktMatrix.ts:118-124`). Die vier
 *     Module aus `migrations/005_domaene.sql:136` sind der vollstaendige Bestand. Ein Anlegeknopf
 *     hier waere ein Bedienelement ohne Endpunkt.
 *
 * NACHTRAG (Auftrag "Task-Vorlage tatsaechlich anwenden", 2026-08-26): Luecke (b) - "AUS DER VORLAGE
 * ENTSTEHEN HEUTE KEINE AUFGABEN" - ist TEILWEISE geschlossen, NICHT vollstaendig, und der Dialogtext
 * behauptet ab jetzt nur noch das Teilstueck, das GEMESSEN zutrifft:
 *
 *  b) NICHT JEDER BESTELLANLAGE-WEG WENDET DIE VORLAGE AN. `POST /api/orders`
 *     (`legeBestellungAn()`, `backend/src/domaene/orders.ts`, ruft `wendeVorlageAufBestellungAn()`,
 *     `backend/src/domaene/tasks.ts`, INNERHALB derselben Transaktion) tut es - das ist der Weg
 *     hinter dem Knopf "Neue Bestellung" (`Kopfzeile.tsx`) samt der Anlagemaske. FUENF WEITERE Wege
 *     legen ebenfalls `orders`-Zeilen an und bekommen (bewusst NICHT nachgezogen, siehe Review-
 *     Entscheidung im Taskbericht) KEINE Vorlagen-Tasks, weil sie eine eigene, hier nicht
 *     beauftragte fachliche Frage aufwerfen (z. B. ob Split/Sammellieferung Tasks vom
 *     URSPRUNGSAUFTRAG erben sollten, statt einen kompletten frischen Vorlagensatz zu bekommen):
 *     `jtl/schreiben/split.ts#erzeugeGeteilteBestellung` und `jtl/schreiben/
 *     sammellieferung.ts#erzeugeSammelbestellung` (RICHTIGGESTELLT: beide schreiben die Hub-Zeile
 *     ueber ihr EIGENES `INSERT INTO orders DEFAULT VALUES`, NICHT ueber die gleichnamige, aber
 *     ANDERE `legeBestellungAn()` aus `jtl/schreiben/anlegen.ts` - die schreibt nach JTL, nicht in
 *     die Hub-Datenbank), `domaene/bestellungKennung.ts` (das lazy angelegte Hub-Gegenstueck zu
 *     einer in JTL bereits existierenden Bestellung, `GET /api/orders/jtl-<n>`),
 *     `dsv/bestellzuordnungLauf.ts` (automatischer DSV-Abgleich) und `migration/bestellung.ts`
 *     (der EINMALIGE Migrationslauf aus dem Altbestand - der uebernimmt Vorlagenzeilen zwar direkt
 *     als Tasks, siehe `migration/aufgaben.ts`, aber ohne `wendeVorlageAufBestellungAn` zu rufen).
 *     Der Dialogtext unten nennt deshalb ausdruecklich "Neue Bestellung" statt pauschal "jede neu
 *     angelegte Bestellung" - sonst waere er wieder die zu weit gefasste Behauptung, die Luecke (b)
 *     vor diesem Auftrag schon einmal war.
 *
 * Was die gespeicherten Zeilen VOR diesem Auftrag schon bewirkten, gilt unveraendert fort: sie sind
 * ZUGLEICH die Nachschlagetabelle der Verantwortungsuebertragung (das SQL oben liest `zeile->>'id'`
 * gegen `order_tasks.template_row_id` und `zeile->>'responsibilityId'`) - eine Zeile, deren Modul
 * hier gewechselt wird, zieht beim naechsten Modul-Schreiben die daraus entstandenen offenen Tasks
 * mit.
 *
 * ============================== DREI ABWEICHUNGEN VOM BESTAND, JEDE MIT GRUND ==============================
 *
 * 1. VERANTWORTUNG HAENGT AN NUTZER-IDs, NICHT AN NAMEN. Der Bestand fuehrte `ownerName`/
 *    `deputyName` als freien Text neben der Id (index.html:5641f.); die Tabelle kennt nur
 *    `owner_user_id`/`deputy_user_id` als `REFERENCES users(id)` (005_domaene.sql:122f.). Die
 *    Auswahl bietet deshalb ausschliesslich AKTIVE Konten an (wie `openTaskTemplateManager`:
 *    `(data.users || []).filter(user => user.active)`), und "Nicht festgelegt" schickt ein
 *    ausdrueckliches `null`.
 * 2. DIE MODULZUORDNUNG DER VORGABEZEILEN LAEUFT UEBER DEN MODULNAMEN. `inferredTaskResponsibility`
 *    (index.html:5464-5470) liefert feste Kennungen wie `responsibility_payment`; in dieser
 *    Datenbank tragen Module UUIDs. Die vier Namen sind zwischen Bestand und Migration jedoch
 *    WORTGLEICH (005_domaene.sql:137-140), also loest `vermutetesModul` ueber den Namen auf. Diese
 *    Vermutung wirkt AUSSCHLIESSLICH beim ERSTEN Aufbau einer noch nicht vorhandenen Vorlage - fuer
 *    gespeicherte Zeilen wird nie geraten (Spec Zeile 691f.: "Es wird NICHT ueber Namensaehnlichkeit
 *    geraten").
 * 3. DIE UEBERTRAGUNG AUF OFFENE TASKS MACHT DER SERVER. Der Bestand lief dafuer selbst durch
 *    `DB.orders` (index.html:5667-5683). Hier genuegt der PATCH; ein Nachbau im Client waere ein
 *    zweiter, konkurrierender Wahrheitsort.
 *
 * ============================== LAND-REITER EU/CHINA (Auftrag "Taskvorlagen je Land (EU/China) =====
 * ============================== getrennt pflegbar", 2026-09-01) =====================================
 *
 * Bis zu diesem Auftrag gab es GENAU EINE Vorlage (`aktiveVorlage()` unten). `Aufgabenvorlage.land`
 * ist seither ein Pflichtfeld (`"eu" | "china"`, `api/aufgabenVorlagen.ts`), `GET /api/task-templates`
 * liefert bis zu ZWEI Zeilen - "China" bleibt die Vorgabe (dort steckt der bisherige, einzige
 * Inhalt), "EU" existiert serverseitig erst NACH dem ersten Speichern dort. Der schmale Reiter ganz
 * oben im Dialog (`aktivesLand`) waehlt seither, WELCHE der beiden Zeilen bearbeitet wird - die
 * Variable, die vorher "die eine Vorlage" hielt, ist jetzt `vorlagen.find(v => v.land ===
 * aktivesLand)`. Die Zustaendigkeitsmodule bleiben davon UNBERUEHRT (global, nicht laenderbezogen).
 *
 * OPTISCH AN DEN KOPFZEILEN-FLAGGEN ORIENTIERT, OHNE DEREN ABHAENGIGKEITEN: dieselbe `<Flagge>`-
 * Komponente (`components/Flagge.tsx`) wie in `App/Kopfzeile.tsx`s Laenderfilter-Chips - "gleiche
 * Symbole, kein Bruch im Look" -, aber eine EIGENE, kleine Bauart (`.task-template-land-tabs`/
 * `-tab`, `styles/neubau/vorlagenLandReiter.css`) statt einer Wiederverwendung von
 * `.kopfzeile-laender-chip`: die Kopfzeilen-Chips sind zwei UNABHAENGIGE Ein-/Ausschalter
 * (`aria-pressed`, beide gleichzeitig moeglich), dieser Reiter hier ist dagegen eine ECHTE
 * Einfachauswahl (`role="tab"`/`aria-selected`, immer genau ein Land aktiv) - ein anderes
 * Bedienmuster fuer eine andere Frage. KEINE Abhaengigkeit von `state/arbeitsplatz.ts` (das ist reine
 * Sichtbarkeits-Filterlogik fuer Bestellungen, nicht fuer Vorlagenpflege) oder von `laenderfilter.css`
 * selbst.
 *
 * VORGABEZEILEN-RUECKFALL BLEIBT AN "CHINA" GEBUNDEN, NICHT AN "KEINE VORLAGE GEFUNDEN": ohne
 * gespeicherte Vorlage zeigte der Dialog VOR diesem Auftrag die 59 Standardaufgaben aus
 * `vorgabeZeilen()` (Test (e) haelt das fest, unveraendert gruen). Fuer "EU noch nie gespeichert"
 * verlangt der Auftrag dagegen ausdruecklich eine LEERE Aufgabenliste (Abnahmekriterium 2) - beide
 * Vorgaben zusammen ergeben `zeilenFuerLand()` unten: der Vorgabezeilen-Rueckfall gilt NUR fuer
 * `aktivesLand === VORGABE_LAND` ("china", die einzige Vorbestands-Bedeutung), "eu" ohne Zeile bleibt
 * leer. Kein Wechsel-Bestaetigungsdialog: der Dialog kennt heute KEIN "ungespeicherte Aenderungen"-
 * Muster (weder beim Schliessen noch sonstwo) - ein Reiterwechsel verwirft lokale, unbestaetigte
 * Bearbeitungen deshalb kommentarlos, wie es ein Schliessen ohne Speichern heute ebenfalls tut.
 */
import { useEffect, useRef, useState, type JSX } from "react";
import { DEFAULT_ORDER_TASK_PHASES } from "../../config/orderTaskPhases.ts";
import { hatRecht } from "../../state/berechtigungen.ts";
import { meldeFehler } from "../../state/fehlerStapel.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { Ikone } from "../Ikone.tsx";
import { Flagge } from "../Flagge.tsx";
import { nutzerListe } from "../../api/nutzer.ts";
import type { Nutzer } from "../../state/reduzierer.ts";
import { ApiFehler, KonfliktFehler, SitzungsFehler } from "../../api/basis.ts";
import { Ladefehler } from "../Ladefehler/Ladefehler.tsx";
import {
  moduleLaden,
  modulSchreiben,
  type Zustaendigkeitsmodul,
} from "../../api/zustaendigkeitsModule.ts";
import {
  taskVorlagenLaden,
  vorlageAnlegen,
  vorlageSchreiben,
  type Aufgabenvorlage,
  type VorlagenZeile,
} from "../../api/aufgabenVorlagen.ts";
import {
  neuePositionenZiehenRef,
  positionAbgelegt,
  positionTaste,
  positionZielFokussieren,
  positionZielUeberfahren,
  positionZiehenEnde,
  positionZiehenStart,
  type PositionenZiehenOptionen,
} from "../NewOrder/PositionenZiehen.ts";

/**
 * Ziehoptionen fuer diese Flaeche (Auftrag "Standardaufgaben lassen sich umsortieren",
 * 2026-08-27) - siehe `PositionenZiehen.ts`-Kopfkommentar, Abschnitt "Verallgemeinert fuer
 * VorlagenDialog.tsx": `wurzelSelektor` traegt den Zeilen-CONTAINER (`#taskTemplateRows`,
 * Gegenstueck zur `<table>` der Anlagemaske), `zeilenSelektor` die einzelne `<div>`-Zeile
 * (Gegenstueck zu `<tr>`), `scope` ein eigener `data-item-scope`-Wert - ANDERS als `"new-order"`,
 * damit `positionZielFokussieren` niemals in die Anlagemaske hineinfokussiert, falls beide
 * Flaechen (theoretisch) gleichzeitig im DOM stuenden.
 */
const ZIEHEN_OPTIONEN: PositionenZiehenOptionen = {
  wurzelSelektor: ".task-template-rows",
  zeilenSelektor: ".task-template-row",
  scope: "task-template-row",
};

/** `defaultTaskTemplateEntity().name` (index.html:5493) - der Name einer neu angelegten Vorlage. */
export const VORGABE_VORLAGENNAME = "Standardaufgaben neue Bestellung";

/** `uid()` (index.html:4675) - wie in `Board/ListenEditor.tsx`. */
const uid = (): string => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

/** Die Phasen-Kennungen, gegen die `saveTaskTemplateManager` (index.html:5656) prueft. */
const PHASEN_IDS: ReadonlySet<string> = new Set(DEFAULT_ORDER_TASK_PHASES.map((phase) => phase.id));

/**
 * `inferredTaskResponsibility` (index.html:5464-5470), Wort fuer Wort dieselben vier Muster - nur
 * liefert sie hier den MODULNAMEN statt der Bestandskennung (siehe Kopfkommentar, Abweichung 2).
 */
export function vermutetesModul(name: string): string {
  const wert = String(name || "").toLocaleLowerCase("de-DE");
  if (/zahl|dollar|kurs|buchhaltung|bank/.test(wert)) return "Zahlung & Kurssicherung";
  if (/sped|transport|zoll|container|logistik|fracht|hafen|etd|eta|anliefer|bl\b/.test(wert)) {
    return "Logistik & Import";
  }
  if (/dokument|artwork|manual|certificate|zertifikat|foto|freigabe|ablegen|cloud/.test(wert)) {
    return "Dokumente & Freigaben";
  }
  return "Einkauf & Lieferant";
}

/**
 * `defaultTaskTemplateEntity().tasks` (index.html:5495-5502): die 59 Aufgaben der sechs Phasen, in
 * Anzeigereihenfolge, mit demselben abgeleiteten Zeilenschluessel `<phaseId>-<n>`. Wird NUR
 * gebraucht, wenn es noch gar keine Vorlage gibt.
 */
export function vorgabeZeilen(module: readonly Zustaendigkeitsmodul[]): VorlagenZeile[] {
  const nachName = new Map(module.map((modul) => [modul.name, modul.id]));
  return DEFAULT_ORDER_TASK_PHASES.flatMap((phase) =>
    phase.tasks.map((aufgabe, index) => ({
      id: `${phase.id}-${index + 1}`,
      phaseId: phase.id,
      name: aufgabe.name,
      description: aufgabe.description || "",
      defaultAssignee: "",
      responsibilityId: nachName.get(vermutetesModul(aufgabe.name)) || "",
    })),
  );
}

/** Ein Modul im Entwurf: der Serverstand plus die zwei bearbeitbaren Felder. */
interface Modulentwurf extends Zustaendigkeitsmodul {
  /** Der Stand beim Laden - nur tatsaechlich geaenderte Module werden geschrieben. */
  readonly urOwnerUserId: string | null;
  readonly urDeputyUserId: string | null;
}

/**
 * Waehlt die zu bearbeitende Vorlage aus der Liste des Servers. Der Bestand kannte GENAU EINE
 * (`id === 'default-order-task-template'`, index.html:5507); hier tragen Vorlagen UUIDs, also
 * entscheidet der Name - und ersatzweise die erste Zeile der (alphabetisch sortierten) Liste, damit
 * eine vorhandene, anders benannte Vorlage bearbeitbar bleibt statt still eine zweite daneben
 * anzulegen.
 *
 * SEIT DEM LAND-AUFTRAG (Kopfkommentar, Abschnitt "LAND-REITER EU/CHINA") waehlt die Komponente
 * SELBST nicht mehr ueber diese Funktion aus - `vorlagen.find(v => v.land === aktivesLand)` ersetzt
 * sie dort, weil es jetzt bis zu ZWEI gueltige Vorlagen gibt (eine je Land) statt einer. Die Funktion
 * bleibt exportiert und getestet (Fall (j3)), weil sie nichts Falsches behauptet - nur nichts mehr
 * aufruft.
 */
export function aktiveVorlage(vorlagen: readonly Aufgabenvorlage[]): Aufgabenvorlage | null {
  return vorlagen.find((v) => v.name === VORGABE_VORLAGENNAME) || vorlagen[0] || null;
}

/** Vorgabe-Land beim Oeffnen des Dialogs - "china" traegt den bisherigen, einzigen Bestand. */
const VORGABE_LAND: "eu" | "china" = "china";

/**
 * Der feste Satz zur gescheiterten Nutzerliste (Fehleranzeige Paket 2, Task 8): die Auswahl der Verantwortlichen
 * bietet dann nur "Nicht festgelegt" an. Die Ursache haengt `Ladefehler` an (`fehlertext(ursache, NUTZERLISTE_SATZ)`).
 */
const NUTZERLISTE_SATZ =
  "Die Nutzerliste konnte nicht geladen werden – die Auswahl der Verantwortlichen ist leer.";

/**
 * Die Aufgabenzeilen fuer EIN Land: die gespeicherten Zeilen, falls es dort schon eine Vorlage gibt -
 * sonst fuer `VORGABE_LAND` ("china") die 59 Vorgabezeilen des Bestands (unveraendertes Verhalten,
 * Test (e)), fuer jedes andere Land (heute nur "eu") eine LEERE Liste (Abnahmekriterium 2 des
 * Land-Auftrags - siehe Kopfkommentar, Abschnitt "VORGABEZEILEN-RUECKFALL").
 */
function zeilenFuerLand(
  vorlage: Aufgabenvorlage | undefined,
  land: "eu" | "china",
  module: readonly Zustaendigkeitsmodul[],
): VorlagenZeile[] {
  if (vorlage) return (vorlage.rows || []).map((zeile) => ({ ...zeile }));
  return land === VORGABE_LAND ? vorgabeZeilen(module) : [];
}

export interface VorlagenDialogProps {
  /** `hasPermission('manage_templates')` - der Bestand prueft beim Oeffnen UND beim Speichern. */
  rechte: ReadonlySet<string>;
  /** `closeTaskTemplateManager()` (index.html:5526). */
  onSchliessen: () => void;
  /** `toast(...)` */
  onMeldung: (text: string) => void;
  /** Nach erfolgreichem Speichern - `App.tsx` zieht damit `zustand.taskVorlagen` nach. */
  onGespeichert?: (vorlagen: readonly Aufgabenvorlage[]) => void;
}

export function VorlagenDialog({
  rechte,
  onSchliessen,
  onMeldung,
  onGespeichert,
}: VorlagenDialogProps): JSX.Element {
  const [module, setModule] = useState<Modulentwurf[]>([]);
  const [zeilen, setZeilen] = useState<VorlagenZeile[]>([]);
  /**
   * ALLE vom Server geladenen Vorlagen (seit dem Land-Auftrag bis zu zwei, eine je Land) - `vorlage`
   * unten ist daraus abgeleitet, kein eigener State mehr (Kopfkommentar, "LAND-REITER EU/CHINA").
   */
  const [vorlagen, setVorlagen] = useState<Aufgabenvorlage[]>([]);
  /** Welches Land der Reiter gerade zeigt - Vorgabe "china" (dort steckt der bisherige Bestand). */
  const [aktivesLand, setAktivesLand] = useState<"eu" | "china">(VORGABE_LAND);
  const [konten, setKonten] = useState<Nutzer[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  /** Die Nutzerliste ist gescheitert (nicht 403): eine Zeile an der Auswahl, sperrt nichts. `Ladefehler` filtert 401/Abbruch. */
  const [nutzerlisteFehler, setNutzerlisteFehler] = useState<{ ursache: unknown } | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const [neueZeileId, setNeueZeileId] = useState<string | null>(null);
  /** Traegt den Ziehzustand ueber dragstart -> dragover -> drop/dragend hinweg (`PositionenZiehen.ts`). */
  const ziehenRef = useRef(neuePositionenZiehenRef());

  const darfVerwalten = hatRecht(rechte, "manage_templates");
  /** Die Vorlage des AKTIVEN Reiters - `undefined`, wenn dieses Land noch nie gespeichert wurde. */
  const vorlage = vorlagen.find((v) => v.land === aktivesLand);

  /*
   * EIN Ladevorgang beim Oeffnen, drei Anfragen nebeneinander - `openTaskTemplateManager`
   * (index.html:5510-5521) laedt ebenfalls beim Oeffnen und nicht bei jedem Zeichnen. Der
   * `AbortController` bricht sie ab, wenn der Dialog vorher wieder zugeht; ein `AbortError` darf
   * deshalb NICHT als Ladefehler erscheinen.
   *
   * `nutzerListe()` scheitert fuer Konten ohne `manage_users` mit 403 - der Dialog haengt aber an
   * `manage_templates`. Diese eine Anfrage faellt deshalb auf eine leere Liste zurueck, statt den
   * ganzen Dialog rot zu machen: ohne sie fehlt die Nutzerauswahl, die Standardaufgaben lassen sich
   * trotzdem pflegen. Die zwei uebrigen Anfragen tragen `null`-Rechte (Constraint 9) und koennen
   * an der Berechtigung nicht scheitern.
   *
   * Das 403 ist der ERWARTETE Fall und bleibt still. Jedes andere Scheitern (5xx, Netz) sagt der Dialog
   * seit Fehleranzeige Paket 2 (Task 8) in einer NICHT sperrenden Zeile an der Auswahl der Verantwortlichen
   * (`nutzerlisteFehler`, `NUTZERLISTE_SATZ` + Ursache) - die sperrende `ladefehler`-Zeile gehoert den Modulen
   * und der Vorlage. Die 403-Weiche steht hier am STATUS: `fehlertext()` liefert fuer ein 403 nicht `null`
   * (es sagt "Dafuer fehlt die Berechtigung" bzw. den Servertext), Abbruch und 401 dagegen schon.
   */
  useEffect(() => {
    const abbruch = new AbortController();
    let lebt = true;
    Promise.all([
      moduleLaden(abbruch.signal),
      taskVorlagenLaden(abbruch.signal),
      nutzerListe(abbruch.signal).then(
        (liste) => ({ liste, ursache: null as unknown }),
        (ursache: unknown) => ({ liste: [] as Nutzer[], ursache }),
      ),
    ])
      .then(([geladeneModule, vorlagen, { liste: nutzer, ursache: nutzerUrsache }]) => {
        if (!lebt) return;
        if (nutzerUrsache !== null && !(nutzerUrsache instanceof ApiFehler && nutzerUrsache.status === 403)) {
          setNutzerlisteFehler({ ursache: nutzerUrsache });
        }
        setModule(
          geladeneModule.map((modul) => ({
            ...modul,
            urOwnerUserId: modul.ownerUserId ?? null,
            urDeputyUserId: modul.deputyUserId ?? null,
          })),
        );
        setKonten(nutzer.filter((konto) => konto.active));
        setVorlagen(vorlagen);
        // `VORGABE_LAND` statt der `aktivesLand`-Zustandsvariable: dieser Effekt laeuft genau einmal
        // beim Oeffnen (Abhaengigkeitsliste `[]`) - zu diesem Zeitpunkt sind beide immer gleich, ein
        // Nutzer kann vor dem ersten Laden noch keinen Reiter gewechselt haben.
        setZeilen(zeilenFuerLand(vorlagen.find((v) => v.land === VORGABE_LAND), VORGABE_LAND, geladeneModule));
        setLaedt(false);
      })
      .catch((fehler: unknown) => {
        if (!lebt || abbruch.signal.aborted) return;
        // Ein 401 hat die Sitzung bereits verworfen und die Anmeldung gezeigt (`api/basis.ts`) -
        // ein zusaetzlicher Fehlertext im Dialog waere irrefuehrend, wie in `MarkenMenue.tsx`.
        if (fehler instanceof SitzungsFehler) return;
        // Die Flaeche steht schon (`empty-note`, R2): neu ist der Text. Der feste Satz wird zum Kontext, die
        // Ursache kommt aus `fehlertext()` (ein Proxy-502 heisst dort "Der Server antwortet gerade nicht ...").
        const kontext = "Die Vorlage konnte nicht geladen werden.";
        setLadefehler(fehlertext(fehler, kontext) ?? kontext);
        setLaedt(false);
      });
    return () => {
      lebt = false;
      abbruch.abort();
    };
  }, []);

  function setzeVerantwortung(
    id: string,
    feld: "ownerUserId" | "deputyUserId",
    wert: string,
  ): void {
    setModule((vorher) =>
      vorher.map((modul) => (modul.id === id ? { ...modul, [feld]: wert || null } : modul)),
    );
  }

  /**
   * Reiterwechsel EU/China (Kopfkommentar, "LAND-REITER EU/CHINA"): kein Bestaetigungsdialog - dieser
   * Dialog kennt heute an KEINER Stelle ein "ungespeicherte Aenderungen"-Muster, ein Reiterwechsel
   * verwirft lokale Bearbeitungen deshalb kommentarlos, wie es ein Schliessen ohne Speichern auch tut.
   */
  function landWechseln(naechstesLand: "eu" | "china"): void {
    if (naechstesLand === aktivesLand) return;
    setAktivesLand(naechstesLand);
    setZeilen(zeilenFuerLand(vorlagen.find((v) => v.land === naechstesLand), naechstesLand, module));
    setNeueZeileId(null);
  }

  /** `patchTaskTemplateRow` (index.html:5615) */
  function setzeZeile(id: string, feld: keyof VorlagenZeile, wert: string): void {
    setZeilen((vorher) => vorher.map((zeile) => (zeile.id === id ? { ...zeile, [feld]: wert } : zeile)));
  }

  /** `addTaskTemplateRow` (index.html:5620) - dieselben Vorgabewerte, `phaseId: 'general'`. */
  function zeileErgaenzen(): void {
    const id = uid();
    setZeilen((vorher) => [
      ...vorher,
      { id, phaseId: "general", name: "", description: "", defaultAssignee: "", responsibilityId: "" },
    ]);
    setNeueZeileId(id);
  }

  /** `removeTaskTemplateRow` (index.html:5627) */
  function zeileEntfernen(id: string): void {
    setZeilen((vorher) => vorher.filter((zeile) => zeile.id !== id));
  }

  /**
   * `saveTaskTemplateManager` (index.html:5633-5687) - dieselbe Reihenfolge aus Pruefen,
   * Normalisieren und Schreiben, mit den im Kopfkommentar benannten Abweichungen.
   *
   * ZWEI SCHREIBVORGAENGE OHNE GEMEINSAME KLAMMER, ausdruecklich benannt: Module und Vorlage sind
   * zwei Ressourcen mit eigener `rowVersion`; es gibt keinen Endpunkt, der beide zusammen schreibt.
   * Scheitert der zweite, sind die Module bereits geschrieben - die Fehlermeldung sagt das dann
   * WOERTLICH, statt "konnte nicht gespeichert werden" zu melden und den Nutzer glauben zu lassen,
   * es sei nichts geschehen.
   */
  async function speichern(): Promise<void> {
    if (!darfVerwalten) {
      // Die Absicht (Speichern) wird nicht ausgefuehrt: Stapel statt 2,2-s-Toast (Klassifikation, S-Zeile).
      meldeFehler("Für das Task-Template fehlt die Berechtigung");
      return;
    }
    // index.html:5648 - dieselbe Sperre, nur gegen Nutzer-IDs statt gegen freie Namen.
    if (module.some((modul) => modul.ownerUserId && modul.ownerUserId === modul.deputyUserId)) {
      meldeFehler("Hauptverantwortung und Vertretung müssen unterschiedliche Nutzer sein");
      return;
    }
    const modulIds = new Set(module.map((modul) => modul.id));
    // index.html:5652-5661: Phase auf 'general' zurueckfallen lassen, Namen trimmen, Zeilen ohne
    // Namen verwerfen. `responsibilityId` faellt auf "" zurueck, wenn das Modul nicht (mehr) da ist.
    // `.map()` auf `zeilen` in DESSEN Reihenfolge - das ist zugleich der Transportweg der Umsortierung
    // (Auftrag "Standardaufgaben lassen sich umsortieren", 2026-08-27): der Ziehgriff/Tastaturweg
    // aendert nur `zeilen` selbst (`setZeilen(...)`, siehe unten bei `taskTemplateRows`), kein
    // eigener Netzwerkaufruf - die neue Reihenfolge geht beim naechsten Speichern automatisch mit,
    // weil hier ohne Sortierung 1:1 aus `zeilen` gelesen wird. Gegengeprueft (nicht nur angenommen):
    // `vorlageSchreiben`/`vorlageAnlegen` (api/aufgabenVorlagen.ts) serialisieren `rows` als JSON-Array
    // ohne eigene Umsortierung; der Test "die Reihenfolge nach dem Umsortieren steckt in der
    // gesendeten rows-Liste beim Speichern" (vorlagen.test.tsx) prueft das gegen den TATSAECHLICH
    // gesendeten Anfragekoerper, nicht nur gegen den Client-Zustand.
    const fertigeZeilen: VorlagenZeile[] = zeilen
      .map((zeile) => ({
        id: String(zeile.id || uid()),
        phaseId: PHASEN_IDS.has(zeile.phaseId) ? zeile.phaseId : "general",
        name: String(zeile.name || "").trim(),
        description: String(zeile.description || ""),
        defaultAssignee: String(zeile.defaultAssignee || ""),
        responsibilityId: modulIds.has(zeile.responsibilityId) ? zeile.responsibilityId : "",
      }))
      .filter((zeile) => zeile.name);
    /*
     * Befund 1 (Pruefer, 2026-09-01): die "Mindestens eine Standardaufgabe"-Sperre (index.html:5662)
     * stand bislang HIER, VOR den Modul-PATCHes - auf einem noch nie gespeicherten Land-Reiter (leere
     * Aufgabenliste, `vorlage` `undefined`) verhinderte sie damit faelschlich SOGAR das Speichern
     * eines reinen Modul-Owner-Wechsels, mit dem die (laenderspezifische) Aufgabenliste gar nichts zu
     * tun hatte. Die Pruefung wandert deshalb hinter die Modul-PATCHes (siehe dort, kurz vor dem
     * Vorlagen-Schreibvorgang) und greift nur noch, wenn es an der Aufgabenliste SELBST etwas zu
     * speichern gaebe - also wenn bereits eine gespeicherte Vorlage existiert, die durch die leere
     * Liste geleert wuerde. Fehlt eine Vorlage fuer dieses Land ganz, gibt es dort nichts zu
     * speichern und folglich auch nichts zu verhindern.
     */
    setSpeichert(true);
    let uebertragen = 0;
    let moduleGeschrieben = 0;
    const zuSchreiben = module.filter(
      (modul) => modul.ownerUserId !== modul.urOwnerUserId || modul.deputyUserId !== modul.urDeputyUserId,
    );
    try {
      // NACHEINANDER und nicht per Promise.all: jeder PATCH loest serverseitig eine Uebertragung auf
      // offene Tasks aus (dieselben Zeilen koennen von zwei Modulen betroffen sein), und beim
      // Fehlschlag soll die Meldung sagen koennen, wie viele Module bereits durch sind: "<n> von <gesamt>
      // Zuständigkeiten gespeichert - die übrigen konnten nicht gespeichert werden." (`gesamt` zaehlt nur die
      // Module mit einer Aenderung; `moduleGeschrieben` zaehlt die bereits geschriebenen).
      for (const modul of zuSchreiben) {
        const antwort = await modulSchreiben(
          modul.id,
          { ownerUserId: modul.ownerUserId, deputyUserId: modul.deputyUserId },
          modul.rowVersion,
        );
        uebertragen += antwort.uebertrageneTasks || 0;
        moduleGeschrieben += 1;
        /*
         * Der geschriebene Stand wird SOFORT zum Ausgangsstand - neue `rowVersion`, neue `ur*`.
         * Ohne diese Zeilen liefe ein ZWEITER Speicherversuch (der Fall gibt es wirklich: der
         * Vorlagen-Schreibvorgang darunter kann scheitern und laesst den Dialog dann offen) mit
         * einer bereits verbrauchten `rowVersion` los und bekaeme einen 409 auf ein Modul, an dem
         * niemand etwas geaendert hat. Mit ihnen ist das Modul beim zweiten Versuch schlicht
         * unveraendert und wird uebersprungen.
         */
        setModule((vorher) =>
          vorher.map((eintrag) =>
            eintrag.id === modul.id
              ? {
                  ...eintrag,
                  rowVersion: antwort.rowVersion,
                  urOwnerUserId: modul.ownerUserId,
                  urDeputyUserId: modul.deputyUserId,
                }
              : eintrag,
          ),
        );
      }
    } catch (fehler: unknown) {
      setSpeichert(false);
      meldeFehler(
        fehler instanceof KonfliktFehler ? VORLAGE_KONFLIKT : fehler,
        moduleGeschrieben > 0
          ? `${moduleGeschrieben} von ${zuSchreiben.length} Zuständigkeiten gespeichert – die übrigen konnten nicht gespeichert werden.`
          : "Die Zuständigkeit konnte nicht gespeichert werden.",
      );
      return;
    }

    if (!fertigeZeilen.length) {
      setSpeichert(false);
      if (vorlage) {
        // Es gibt bereits eine gespeicherte Vorlage fuer dieses Land, die durch die leere Liste
        // geleert wuerde - das bleibt ungueltig (index.html:5662), blockiert aber NICHT mehr die
        // Module, die oben bereits geschrieben sind.
        meldeFehler(
          moduleGeschrieben > 0
            ? `Die Zuständigkeit ist gespeichert (${moduleGeschrieben} ${moduleGeschrieben === 1 ? "Modul" : "Module"}), die Standardaufgaben nicht: Mindestens eine Standardaufgabe ist erforderlich.`
            : "Mindestens eine Standardaufgabe ist erforderlich",
        );
        return;
      }
      // Noch keine gespeicherte Vorlage fuer dieses Land (z. B. ein frischer "EU"-Reiter) UND keine
      // Zeilen - an der Aufgabenliste selbst gibt es dann nichts zu speichern, ein `vorlageAnlegen(...)`
      // mit leeren `rows` waere ein Artefakt, das niemand angefordert hat (Befund 1, Pruefer 2026-09-01).
      onMeldung(
        moduleGeschrieben > 0
          ? `Zuständigkeit gespeichert (${moduleGeschrieben} ${moduleGeschrieben === 1 ? "Modul" : "Module"})${uebertragen > 0 ? ` · ${uebertragen} offene ${uebertragen === 1 ? "Aufgabe" : "Aufgaben"} übernommen` : ""}`
          : "Keine Änderungen zum Speichern",
      );
      onSchliessen();
      return;
    }

    try {
      const geschrieben = vorlage
        ? await vorlageSchreiben(vorlage.id, { rows: fertigeZeilen }, vorlage.rowVersion)
        : await vorlageAnlegen(VORGABE_VORLAGENNAME, fertigeZeilen, aktivesLand);
      onGespeichert?.([geschrieben]);
    } catch (fehler: unknown) {
      setSpeichert(false);
      meldeFehler(
        fehler instanceof KonfliktFehler ? VORLAGE_KONFLIKT : fehler,
        moduleGeschrieben > 0
          ? `Die Zuständigkeit ist gespeichert (${moduleGeschrieben} ${moduleGeschrieben === 1 ? "Modul" : "Module"}), die Standardaufgaben nicht.`
          : "Die Standardaufgaben konnten nicht gespeichert werden.",
      );
      return;
    }

    setSpeichert(false);
    onMeldung(
      uebertragen > 0
        ? `Task-Template gespeichert · ${uebertragen} offene ${uebertragen === 1 ? "Aufgabe" : "Aufgaben"} übernommen`
        : "Task-Template gespeichert",
    );
    onSchliessen();
  }

  return (
    <div
      id="taskTemplateOverlay"
      className="board-editor-overlay open"
      role="presentation"
      onClick={(ereignis) => {
        if (ereignis.target === ereignis.currentTarget) onSchliessen();
      }}
    >
      <section
        className="board-editor-dialog task-template-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="taskTemplateTitle"
      >
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id="taskTemplateTitle">Task-Template verwalten</h2>
            {/*
              NICHT der volle Satz des Bestands ("... bei JEDER neu angelegten Bestellung ...") -
              siehe Kopfkommentar, Luecke (b): fuenf weitere Anlagewege (Split, Sammellieferung, die
              lazy angelegte jtl-<n>-Hub-Zeile, der DSV-Abgleich, der einmalige Migrationslauf) wenden
              die Vorlage NICHT an. Der Satz hier nennt deshalb ausdruecklich den Weg, der es tut.
            */}
            <p>
              Diese Vorlage wird übernommen, wenn eine Bestellung neu über „Neue Bestellung" angelegt
              wird. Hauptverantwortung und Vertretung je Modul werden beim Speichern zusätzlich auf
              alle offenen Aufgaben dieses Moduls übertragen. Erledigte Aufgaben bleiben als Historie
              unverändert.
            </p>
          </div>
          <button
            className="icon-btn"
            type="button"
            onClick={onSchliessen}
            title="Task-Template schließen"
          >
            <Ikone name="x" />
          </button>
        </div>
        <div className="board-editor-body task-template-body">
          {ladefehler ? (
            <div className="empty-note">{ladefehler}</div>
          ) : laedt ? (
            <div className="empty-note">Vorlage wird geladen…</div>
          ) : (
            <>
              {/*
                Land-Reiter EU/China, GANZ OBEN (Kopfkommentar, "LAND-REITER EU/CHINA") - waehlt, ob
                die China- oder die EU-Vorlage darunter bearbeitet wird. Die Zustaendigkeitsmodule
                darunter bleiben davon UNBERUEHRT (global, nicht laenderbezogen).
              */}
              <div className="task-template-land-tabs" role="tablist" aria-label="Land der Vorlage">
                <button
                  type="button"
                  role="tab"
                  className="task-template-land-tab"
                  aria-selected={aktivesLand === "eu"}
                  onClick={() => landWechseln("eu")}
                >
                  <Flagge region="eu" aria-hidden="true" />
                  EU
                </button>
                <button
                  type="button"
                  role="tab"
                  className="task-template-land-tab"
                  aria-selected={aktivesLand === "china"}
                  onClick={() => landWechseln("china")}
                >
                  <Flagge region="china" aria-hidden="true" />
                  China
                </button>
              </div>
              <div className="responsibility-manager">
                <div className="responsibility-manager-head">
                  <div>
                    <strong>Zuständigkeitsmodule</strong>
                    <span>
                      Ein Modul bündelt Tasks und legt Hauptverantwortung sowie Vertretung zentral
                      fest.
                    </span>
                  </div>
                  {/*
                    GESPERRT statt weggelassen - Luecke (a) des Kopfkommentars sichtbar gemacht:
                    es gibt kein `POST /api/responsibility-modules`.
                  */}
                  <button
                    className="btn-ghost icon-label"
                    type="button"
                    disabled
                    title="Module lassen sich nicht anlegen – es gibt keinen Endpunkt dafür (GET und PATCH sind die einzigen)"
                  >
                    <Ikone name="blocks" />
                    Modul hinzufügen
                  </button>
                </div>
                {/*
                  Die Nutzerliste ist gescheitert (nicht 403, das ist der erwartete Fall ohne manage_users):
                  eine Zeile an der Auswahl, die sonst leer aussaehe. Ohne "Erneut laden" und ohne Sperre -
                  Zustaendigkeiten und Standardaufgaben bleiben bedienbar, der Dialog laedt nur beim Oeffnen.
                */}
                {nutzerlisteFehler !== null ? (
                  <Ladefehler was={NUTZERLISTE_SATZ} ursache={nutzerlisteFehler.ursache} />
                ) : null}
                <div className="responsibility-module-labels" aria-hidden="true">
                  <span>Modul</span>
                  <span>Hauptverantwortlich</span>
                  <span>Vertretung</span>
                  <span />
                </div>
                <div id="responsibilityModuleRows" className="responsibility-module-list">
                  {module.length === 0 ? (
                    <div className="empty-note">Noch keine Zuständigkeitsmodule vorhanden.</div>
                  ) : (
                    module.map((modul) => (
                      <div className="responsibility-module-row" key={modul.id}>
                        <input
                          value={modul.name}
                          readOnly
                          aria-label="Modulname"
                          title="Modulnamen sind fest vergeben – Umbenennen ist über die API nicht vorgesehen"
                        />
                        <select
                          aria-label={`Hauptverantwortlich für ${modul.name}`}
                          value={modul.ownerUserId ?? ""}
                          disabled={!darfVerwalten}
                          onChange={(ereignis) =>
                            setzeVerantwortung(modul.id, "ownerUserId", ereignis.target.value)
                          }
                        >
                          <KontoOptionen konten={konten} gewaehlt={modul.ownerUserId} />
                        </select>
                        <select
                          aria-label={`Vertretung für ${modul.name}`}
                          value={modul.deputyUserId ?? ""}
                          disabled={!darfVerwalten}
                          onChange={(ereignis) =>
                            setzeVerantwortung(modul.id, "deputyUserId", ereignis.target.value)
                          }
                        >
                          <KontoOptionen konten={konten} gewaehlt={modul.deputyUserId} />
                        </select>
                        <button
                          className="responsibility-module-delete"
                          type="button"
                          disabled
                          title="Module lassen sich nicht löschen – es gibt keinen Endpunkt dafür"
                          aria-label="Modul löschen"
                        >
                          <Ikone name="trash-2" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
              {/*
                Sortierhinweis, dasselbe Muster wie `PositionsEditor.tsx` (Anlagemaske) -
                `items-table-tools`/`items-sort-hint` sind generisch genug fuer eine zweite Flaeche,
                keine eigene Regel dafuer noetig (siehe styles/neubau/vorlagenZeilenZiehen.css).
              */}
              <div className="items-table-tools">
                <span className="items-sort-hint">
                  <Ikone name="arrow-up-down" />
                  Standardaufgaben am Griff ziehen
                </span>
              </div>
              <div className="task-template-columns" aria-hidden="true">
                <span />
                <span>Phase &amp; Aufgabe</span>
                <span>Zuständigkeitsmodul</span>
                <span>Aktion</span>
              </div>
              <div id="taskTemplateRows" className="task-template-rows">
                {zeilen.length === 0 ? (
                  <div className="empty-note">
                    Noch keine Standardaufgaben. Über den Button unten kann eine Aufgabe hinzugefügt
                    werden.
                  </div>
                ) : (
                  zeilen.map((zeile, index) => {
                    const nr = index + 1;
                    return (
                    <div
                      className="task-template-row"
                      key={zeile.id}
                      data-item-scope={ZIEHEN_OPTIONEN.scope}
                      data-item-index={String(index)}
                      onDragOver={(ereignis) =>
                        positionZielUeberfahren(ereignis, index, ziehenRef.current, ZIEHEN_OPTIONEN)
                      }
                      onDrop={(ereignis) => {
                        const naechste = positionAbgelegt(ereignis, zeilen, ziehenRef.current, ZIEHEN_OPTIONEN);
                        if (!naechste) return;
                        setZeilen(naechste);
                      }}
                    >
                      <div className="task-template-drag-cell">
                        {/*
                          Guard gegen `speichert` (Auftrag, eigene Einschaetzung - siehe Kopfkommentar
                          "DRITTER RIEGEL" in PositionsEditor.tsx fuer die Bauart-Vorlage): ANDERS als
                          dort gibt es hier KEINE asynchrone VOLLSTAENDIGE Ersetzung von `zeilen`
                          waehrend `speichert` lief - `fertigeZeilen` (in `speichern()`) wird SYNCHRON
                          aus dem Zustand VOR `setSpeichert(true)` berechnet, ein laufender PATCH liest
                          `zeilen` also kein zweites Mal. Ein Umsortieren waehrend des Speicherns kann
                          die bereits gesendete Anfrage folglich NICHT verfaelschen. Der Riegel steht
                          trotzdem, aus zwei schwaecheren, aber echten Gruenden: (1) Konsistenz mit dem
                          Speichern-Knopf, der waehrend `speichert` ebenfalls gesperrt ist - eine Zeile
                          sollte sich waehrend eines laufenden Schreibvorgangs nicht bewegen, auch wenn
                          es technisch folgenlos bliebe; (2) Vorwaertssicherheit, falls `speichern()`
                          `zeilen` kuenftig ein zweites Mal liest (z. B. bei einem Retry-Pfad) - dann
                          waere der Riegel bereits da, statt erst nach einem neuen Fehlerbild ergaenzt
                          werden zu muessen. Dieselbe Dreifach-Bauart wie im Vorbild (draggable UND
                          Wache in onDragStart UND in onKeyDown), aus Konsistenz mit dessen Muster,
                          auch wenn hier nur EIN Riegel wirklich noetig waere.
                        */}
                        <button
                          className="item-drag-handle"
                          type="button"
                          draggable={!speichert}
                          aria-label={`Standardaufgabe ${nr} verschieben`}
                          title="Ziehen oder mit Pfeiltasten verschieben"
                          onDragStart={(ereignis) => {
                            if (speichert) {
                              ereignis.preventDefault();
                              return;
                            }
                            positionZiehenStart(ereignis, zeilen, index, ziehenRef.current, ZIEHEN_OPTIONEN);
                          }}
                          onDragEnd={(ereignis) => positionZiehenEnde(ereignis, ziehenRef.current, ZIEHEN_OPTIONEN)}
                          onKeyDown={(ereignis) => {
                            if (speichert) return;
                            const ergebnis = positionTaste(ereignis, zeilen, index);
                            if (!ergebnis) return;
                            setZeilen(ergebnis.positionen);
                            positionZielFokussieren(ergebnis.fokusIndex, ZIEHEN_OPTIONEN);
                          }}
                        >
                          <Ikone name="grip-vertical" />
                        </button>
                      </div>
                      <div className="task-template-main">
                        <select
                          aria-label="Phase"
                          value={PHASEN_IDS.has(zeile.phaseId) ? zeile.phaseId : "general"}
                          onChange={(ereignis) =>
                            setzeZeile(zeile.id, "phaseId", ereignis.target.value)
                          }
                        >
                          {DEFAULT_ORDER_TASK_PHASES.map((phase) => (
                            <option key={phase.id} value={phase.id}>
                              {phase.title}
                            </option>
                          ))}
                        </select>
                        <input
                          value={zeile.name}
                          placeholder="Aufgabenname"
                          maxLength={240}
                          aria-label="Aufgabenname"
                          autoFocus={zeile.id === neueZeileId}
                          onChange={(ereignis) => setzeZeile(zeile.id, "name", ereignis.target.value)}
                        />
                        <textarea
                          placeholder="Beschreibung, Hinweise oder Mailvorlage"
                          aria-label="Beschreibung"
                          value={zeile.description}
                          onChange={(ereignis) =>
                            setzeZeile(zeile.id, "description", ereignis.target.value)
                          }
                        />
                      </div>
                      <select
                        aria-label="Zuständigkeitsmodul"
                        value={zeile.responsibilityId}
                        onChange={(ereignis) =>
                          setzeZeile(zeile.id, "responsibilityId", ereignis.target.value)
                        }
                      >
                        <option value="">Kein Modul / direkt zuweisen</option>
                        {module.map((modul) => (
                          <option key={modul.id} value={modul.id}>
                            {modul.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className="task-template-delete"
                        type="button"
                        onClick={() => zeileEntfernen(zeile.id)}
                        title="Standardaufgabe löschen"
                        aria-label="Standardaufgabe löschen"
                      >
                        <Ikone name="trash-2" />
                      </button>
                    </div>
                    );
                  })
                )}
              </div>
              <button className="board-editor-add icon-label" type="button" onClick={zeileErgaenzen}>
                <Ikone name="circle-plus" />
                Neue Standardaufgabe
              </button>
              <div className="board-editor-note">
                Änderungen an einem Modul werden beim Speichern auf alle noch offenen Tasks dieses
                Moduls übertragen. Erledigte Tasks bleiben als Historie unverändert. Die
                Standardaufgaben legen bei einer neuen Bestellung über „Neue Bestellung" automatisch
                Aufgaben an; über „Vorlage anwenden" im Tasks-Reiter einer Bestellung lassen sich neu
                ergänzte Zeilen auch nachträglich anwenden.
              </div>
            </>
          )}
        </div>
        <div className="board-editor-foot">
          <button className="btn-sec" type="button" onClick={onSchliessen}>
            <Ikone name="x" />
            Abbrechen
          </button>
          <button
            className="btn-primary"
            type="button"
            onClick={() => void speichern()}
            disabled={laedt || speichert || !!ladefehler || !darfVerwalten}
            title={darfVerwalten ? undefined : "fehlende Berechtigung"}
          >
            <Ikone name="save" />
            {speichert ? "Wird gespeichert…" : "Template speichern"}
          </button>
        </div>
      </section>
    </div>
  );
}

/**
 * `taskTemplateUserOptions` (index.html:5536-5546): "Nicht festgelegt" zuerst, danach die aktiven
 * Konten - und, falls der gespeicherte Nutzer nicht (mehr) in der Liste steht, ein zusaetzlicher
 * Eintrag fuer genau ihn, damit die Auswahl ihn nicht stillschweigend auf "Nicht festgelegt"
 * zurueckstellt.
 */
function KontoOptionen({
  konten,
  gewaehlt,
}: {
  konten: readonly Nutzer[];
  gewaehlt: string | null;
}): JSX.Element {
  const fehlt = gewaehlt && !konten.some((konto) => konto.id === gewaehlt);
  return (
    <>
      <option value="">Nicht festgelegt</option>
      {konten.map((konto) => (
        <option key={konto.id} value={konto.id}>
          {konto.displayName || konto.username}
        </option>
      ))}
      {fehlt ? <option value={gewaehlt}>Bisheriger Nutzer</option> : null}
    </>
  );
}

/**
 * Ein 409 verdient einen eigenen Satz - er ist kein Netzfehler, sondern ein Fremdzugriff, und der Servertext
 * sagt das nicht. Er geht als URSACHE an `meldeFehler` (statt des `KonfliktFehler`s); der Kontext davor sagt,
 * was nicht gespeichert wurde. Der frühere lokale Helfer `fehlertext()` an dieser Stelle ist durch den
 * zentralen Übersetzer ersetzt (Fehleranzeige Paket 1, Task 9).
 */
const VORLAGE_KONFLIKT =
  "Die Vorlage wurde zwischenzeitlich von jemand anderem geändert. Bitte den Dialog erneut öffnen.";
